// Type definitions for protocol arrays (should eventually be moved to shared types)
export type ProtocolArray = any[];

export interface ServerClientConnector<T> {
    reqSeq: number,
    pendingReqs: {[req: number]: (v: any, error?: any) => void }
    privateId: string,
    publicId: string,
    gotFirstAreas: boolean,
    seat?: ReservedSeat,
    options: T,
    disconnect: () => void,
    send: (msg: string, reliable?: boolean, ordered?: boolean) => void,
    onMessage: (cb: (msg: ProtocolArray) => void) => void,
}
import { PROTOCOLS, bufferToBase64 } from '@gotti/core';
import type { RawBufferInput } from '@gotti/core';
import { getLogger } from '@gotti/core';

import { ConnectorServerConfig, ReservedSeat } from '../servers/ConnectorServer.js';

export abstract class BaseServerConnector<T> {
    readonly logger = getLogger('ServerConnector:', { kind: 'connector' });
    readonly reservedSeats : {[clientId: string]: any } = {};
    readonly clients : Array<ServerClientConnector<T>> = [];
    readonly clientsByPrivateId : {[clientId: string]: ServerClientConnector<T> } = {};
    readonly clientsByPublicId: {[publicId: string]: ServerClientConnector<T> } = {};
    readonly config: ConnectorServerConfig;
    protected _onValidateClient : (privateId: string, publicId: string) => boolean = () => { throw new Error(`Unimplemented`) };
    protected _onWebClientMessage : (client: ServerClientConnector<T>, msg: ProtocolArray) => void = () => { throw new Error(`Unimplemented`) };
    protected _onJoinedClient : (client: ServerClientConnector<T>) => void = () => { throw new Error(`Unimplemented`) };
    protected _onRemovedClient : (client: ServerClientConnector<T>) => void = () => { throw new Error(`Unimplemented`) };
    protected _onRawClientMessage : (client: ServerClientConnector<T>, data: ArrayBuffer) => void = () => { throw new Error(`Unimplemented`) };

    constructor(config: ConnectorServerConfig) {
        this.config = config;
        this.start();
    }

    /**
     * The interface the client-facing socket listens on. `options.*Options.host`
     * is the ADDRESS CLIENTS ARE GIVEN (the gate copies it into every join
     * response — `publicHost` when the config sets one), so it cannot double as
     * the bind once those differ: `bindHost` wins when set, and an unset
     * `bindHost` binds the advertised host exactly as before.
     */
    protected listenHost(advertised: string | undefined): string | undefined {
        return this.config.bindHost ?? advertised;
    }

    public onValidateClient(cb: (privateId: string, publicId: string) => boolean) {
        this._onValidateClient = cb;
    }
    public onWebClientMessage(cb: (client: ServerClientConnector<T>, msg: ProtocolArray) => void ) {
        this._onWebClientMessage = cb;
    }
    public onJoinedClient(cb: (client: ServerClientConnector<T>) => void ) {
        this._onJoinedClient = cb;
    }
    public onRemovedClient(cb: (client: ServerClientConnector<T>) => void ) {
        this._onRemovedClient = cb;
    }
    public onRawClientMessage(cb: (client: ServerClientConnector<T>, data: ArrayBuffer) => void ) {
        this._onRawClientMessage = cb;
    }
    
    public sendToClientNoEncode(clientId: string, msg: string, reliable=false, ordered=false) {
        this.clientsByPrivateId[clientId]?.send(msg, reliable, ordered);
    }
    public sendToClient(clientId: string, msg: Array<any>, reliable=false, ordered=false) {
        this.clientsByPrivateId[clientId]?.send(JSON.stringify(msg), reliable, ordered);
    }

    public sendToClientsByPublicIds(clientIds: string[], msg: string, reliable=false, ordered = false) {
      for(let i = 0; i < clientIds.length; i++) {
          this.clientsByPublicId[clientIds[i]]?.send(msg, reliable, ordered);
      }
  }
    public sendToClients(clientIds: string[], msg: string, reliable=false, ordered = false) {
        for(let i = 0; i < clientIds.length; i++) {
            this.clientsByPrivateId[clientIds[i]]?.send(msg, reliable, ordered);
        }
    }
    public sendRawToClient(clientId: string, data: RawBufferInput | string, reliable = true, ordered = true) {
        const payload = typeof data === 'string' ? data : bufferToBase64(data);
        this.sendToClient(clientId, [PROTOCOLS.SERVER_TO_CLIENT_RAW_MESSAGE, payload], reliable, ordered);
    }

    public sendRawToClients(clientIds: string[], data: RawBufferInput | string, reliable = true, ordered = true) {
        for(let i = 0; i < clientIds.length; i++) {
            this.sendRawToClient(clientIds[i], data, reliable, ordered);
        }
    }
    public broadcastAll(msg: string, reliable=false, ordered=false) {
			for(let i = 0; i < this.clients.length; i++) {
				this.clients[i].send(msg, reliable, ordered);
			}
    }

    public async addClient(client: ServerClientConnector<T>) : Promise<ServerClientConnector<T> | null> {
        try {
            client.onMessage((msg: ProtocolArray) => this._onWebClientMessage(client, msg));
            this.clients.push(client);
            this.clientsByPrivateId[client.privateId] = client;
            this.clientsByPublicId[client.publicId] = client;
            this._onJoinedClient(client);
            return client;
        } catch (err) {
            console.error(`Error when adding client: ${err instanceof Error ? err.message : String(err)}`)
        }
        return null;
    }
    public removeClient(id: string) {
        const c = this.clientsByPrivateId[id];
        if(c) {
            delete this.clientsByPrivateId[id];
            delete this.clientsByPublicId[c.publicId];
            const idx = this.clients.indexOf(c);
            if(idx > -1) {
                this.clients.splice(idx, 1);
            }
            c.disconnect();
            this._onRemovedClient(c);
        }
    }
    public abstract close() : Promise<void>;
    public abstract start() : void;

}